<script setup lang="ts">
import { type Ref, computed, inject, provide, ref, useSlots, watch } from 'vue'
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import emblaCarouselVue from 'embla-carousel-vue'

const emblaMainApi: Ref<EmblaCarouselType> | undefined = inject('emblaApi')

const selectedIndex = ref(0)
const leftGradient = ref(false)
const rightGradient = ref(false)

const emblaOptions: EmblaOptionsType = {
    containScroll: 'keepSnaps',
    dragFree: true,
}
const [emblaThumbsRef, emblaThumbsApi] = emblaCarouselVue(emblaOptions)

// provide emblaThumbsApi to slots
provide('emblaThumbsApi', emblaThumbsApi)

// emblaThumbsApi is exposed to the outside and accessible through references, e.g., emblaThumbsRef.value?.emblaThumbsApi.value.scrollNext()
defineExpose({
    emblaThumbsApi,
})

const slots = useSlots()

// calculates the number of slides passed through the default slot
const slidesCount = computed(() => {
    const defaultSlot = slots.default?.()
    if (
        defaultSlot &&
        Array.isArray(defaultSlot) &&
        defaultSlot[0]?.children &&
        Array.isArray(defaultSlot[0].children)
    ) {
        return defaultSlot[0].children.length
    }
    return 0
})

function onThumbClick(index: number): void {
    if (!emblaMainApi?.value || !emblaThumbsApi.value) {
        return
    }
    emblaMainApi.value.scrollTo(index)
}

function updateGradientsBasedOnScroll(): void {
    if (!emblaThumbsApi?.value) {
        return
    }

    const scrollProgress = emblaThumbsApi.value.scrollProgress()

    leftGradient.value = scrollProgress > 0.001
    rightGradient.value = scrollProgress < 0.999
}

function initSlidesAttrs(): void {
    if (!emblaMainApi?.value || !emblaThumbsApi.value) {
        return
    }

    const slides = emblaThumbsApi.value.slideNodes()

    slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `Slajd ${index + 1}`)
        slide.setAttribute('aria-controls', `carousel-item-${index + 1}`)
        slide.setAttribute('id', `carousel-tab-${index + 1}`)

        slide.onclick = (): void => onThumbClick(index)
    })
}

function onSelect(): void {
    if (!emblaMainApi?.value || !emblaThumbsApi.value) {
        return
    }

    const slides = emblaThumbsApi.value.slideNodes()

    // set active class and onThumbClick event
    const borderClass = '!border-brand-accent-on-dark'

    selectedIndex.value = emblaMainApi.value.selectedScrollSnap()

    slides.forEach((slide, index) => {
        slide.classList.remove(/* tw */ borderClass)
        slide.setAttribute('aria-current', 'false')
        slide.setAttribute('tabindex', index === selectedIndex.value ? '0' : '-1') // Set tabindex based on active slide

        // Remove keydown event from non-active slides
        slide.removeEventListener('keydown', onKeydown)
    })

    const activeSlide = slides[selectedIndex.value]

    if (!activeSlide) {
        return
    }

    activeSlide.classList.add(borderClass)
    activeSlide.setAttribute('aria-current', 'true')

    // Scroll to active slide
    emblaThumbsApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap())

    // Add keydown event listener to the active slide
    activeSlide.addEventListener('keydown', onKeydown)

    // Update gradient visibility based on scroll position
    updateGradientsBasedOnScroll()
}

// Keyboard navigation function
function onKeydown(event: KeyboardEvent): void {
    if (!emblaMainApi?.value || !emblaThumbsApi.value) {
        return
    }

    if (event.key === 'ArrowLeft') {
        emblaMainApi.value.scrollPrev()
        event.preventDefault()
    } else if (event.key === 'ArrowRight') {
        emblaMainApi.value.scrollNext()
        event.preventDefault()
    }

    const slides = emblaThumbsApi.value.slideNodes()

    selectedIndex.value = emblaMainApi.value.selectedScrollSnap()

    const activeSlide = slides[selectedIndex.value]

    activeSlide.focus()
}

watch(
    () => emblaMainApi?.value,
    () => {
        if (emblaMainApi?.value) {
            emblaMainApi.value.on('select', onSelect)
            emblaMainApi.value.on('init', onSelect)
            emblaMainApi.value.on('reInit', onSelect)
        }
        if (emblaThumbsApi?.value) {
            emblaThumbsApi.value.on('init', initSlidesAttrs)
            emblaThumbsApi.value.on('reInit', initSlidesAttrs)
            emblaThumbsApi.value.on('select', updateGradientsBasedOnScroll)
            emblaThumbsApi.value.on('scroll', updateGradientsBasedOnScroll)
        }
    },
    { immediate: true },
)
</script>

<template>
    <div
        v-show="slidesCount > 1"
        :class="`relative mt-4 lg:mt-6 max-md:px-4 max-lg:px-6 overflow-hidden
        before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-5 before:bg-gradient-to-r before:from-neutral-primary before:to-comp-primary-ghost before:opacity-0 before:pointer-events-none
        after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-5 after:bg-gradient-to-l after:from-neutral-primary after:to-comp-primary-ghost after:opacity-0 after:pointer-events-none
        ${leftGradient && 'lg:before:opacity-100'}
        ${rightGradient && 'lg:after:opacity-100'}
        `"
    >
        <div
            ref="emblaThumbsRef"
            aria-roledescription="Karuzela"
            aria-live="polite"
            role="region"
            aria-atomic="true"
            class="p-1"
        >
            <div
                id="thumbs-slides"
                class="gap-horizontal-card-group flex"
            >
                <slot />
            </div>
        </div>
    </div>
</template>
